/**
 * /politique-cookies — Politique de cookies SmartLawyer
 */

import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { PublicHeader, PublicFooter } from '@/components/jurisprudence';
import { Breadcrumb } from '@/components/jurisprudence/Breadcrumb';

const SITE_URL = 'https://smartlawyer.ai';

const COOKIE_ROWS = [
  { name: 'session_token', id: 'session' },
  { name: 'auth_token',    id: 'auth' },
  { name: 'theme',         id: 'theme' },
];

const DONT_ITEMS = ['ads', 'sharing', 'trackers', 'selling', 'no_ads'];

export function PolitiqueCookies() {
  const { t } = useTranslation();
  return (
    <>
      <Helmet>
        <title>{t('legal.cookies.meta_title')}</title>
        <meta name="description" content={t('legal.cookies.meta_description')} />
        <link rel="canonical" href={`${SITE_URL}/politique-cookies`} />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <div className="min-h-screen bg-background flex flex-col">
        <PublicHeader />

        <Breadcrumb items={[
          { label: t('menu.home'),            to: '/' },
          { label: t('legal.cookies.title') },
        ]} />

        <main className="flex-1 max-w-3xl mx-auto w-full px-4 sm:px-6 py-12">
          <h1 className="text-3xl font-bold mb-2">{t('legal.cookies.title')}</h1>
          <p className="text-sm text-muted-foreground mb-10">{t('legal.cookies.last_updated')}</p>

          <div className="space-y-10 text-sm leading-relaxed">

            <section>
              <h2 className="text-base font-semibold mb-3">{t('legal.cookies.what.title')}</h2>
              <p className="text-muted-foreground">
                {t('legal.cookies.what.text')}
              </p>
            </section>

            <section>
              <h2 className="text-base font-semibold mb-3">{t('legal.cookies.used.title')}</h2>
              <p className="text-muted-foreground mb-4">
                <Trans
                  i18nKey="legal.cookies.used.text"
                  components={{ strong: <strong className="text-foreground" /> }}
                />
              </p>

              <div className="rounded-xl border overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-muted/50 border-b">
                      <th className="text-left px-4 py-3 font-semibold text-foreground">{t('legal.cookies.table.name')}</th>
                      <th className="text-left px-4 py-3 font-semibold text-foreground">{t('legal.cookies.table.type')}</th>
                      <th className="text-left px-4 py-3 font-semibold text-foreground">{t('legal.cookies.table.purpose')}</th>
                      <th className="text-left px-4 py-3 font-semibold text-foreground">{t('legal.cookies.table.duration')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y text-muted-foreground">
                    {COOKIE_ROWS.map(({ name, id }) => (
                      <tr key={name}>
                        <td className="px-4 py-3 font-mono">{name}</td>
                        <td className="px-4 py-3">{t(`legal.cookies.table.rows.${id}.type`)}</td>
                        <td className="px-4 py-3">{t(`legal.cookies.table.rows.${id}.purpose`)}</td>
                        <td className="px-4 py-3">{t(`legal.cookies.table.rows.${id}.duration`)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 className="text-base font-semibold mb-3">{t('legal.cookies.basis.title')}</h2>
              <p className="text-muted-foreground">
                {t('legal.cookies.basis.text')}
              </p>
            </section>

            <section>
              <h2 className="text-base font-semibold mb-3">{t('legal.cookies.dont.title')}</h2>
              <ul className="text-muted-foreground space-y-2">
                {DONT_ITEMS.map(item => (
                  <li key={item} className="flex items-start gap-2">
                    <svg className="w-3.5 h-3.5 text-primary flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                    {t(`legal.cookies.dont.items.${item}`)}
                  </li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className="text-base font-semibold mb-3">{t('legal.cookies.manage.title')}</h2>
              <p className="text-muted-foreground">
                {t('legal.cookies.manage.p1')}
              </p>
              <p className="text-muted-foreground mt-2">
                {t('legal.cookies.manage.p2')}{' '}
                <a href="https://www.cnil.fr/fr/cookies-les-outils-pour-les-maitriser"
                  target="_blank" rel="noopener noreferrer"
                  className="text-primary hover:underline">
                  {t('legal.cookies.manage.cnil_link')}
                </a>
              </p>
            </section>

            <section>
              <h2 className="text-base font-semibold mb-3">{t('legal.cookies.contact.title')}</h2>
              <p className="text-muted-foreground">
                {t('legal.cookies.contact.text')}{' '}
                <a href="mailto:rgpd@smartlawyer.ai" className="text-primary hover:underline">
                  rgpd@smartlawyer.ai
                </a>
              </p>
            </section>

          </div>
        </main>

        <PublicFooter />
      </div>
    </>
  );
}
